import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import Avatar from "@mui/material/Avatar";
import AIChatMessage from "./AIChatMessage";
import AITypingIndicator from "./AITypingIndicator";
import { AI_ASSISTANT_BLUE, STARTER_ACTIONS } from "./constants";

const STARTER_ICONS = {
  "create-quiz": QuizOutlinedIcon,
  "check-quiz": AssessmentOutlinedIcon,
  "ask-quizzes": HelpOutlineRoundedIcon,
  "make-presentation": SlideshowOutlinedIcon,
};

export default function AIChatMessages({
  messages,
  sending,
  restoring,
  sectionLabel,
  onRetry,
  onStarterSelect,
}) {
  const endRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending, restoring]);

  if (restoring) {
    return (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} sx={{ color: AI_ASSISTANT_BLUE }} />
      </Box>
    );
  }

  if (!messages.length && !sending) {
    return (
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflowY: "auto",
          px: 2,
          py: 3,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f8fafc",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2.5 }}>
          <Avatar
            sx={{
              width: 52,
              height: 52,
              mb: 1.5,
              mx: "auto",
              background: `linear-gradient(135deg, ${AI_ASSISTANT_BLUE}, #3b82f6)`,
            }}
          >
            <AutoAwesomeRoundedIcon />
          </Avatar>
          <Typography fontWeight={700} sx={{ color: "#0f172a", mb: 0.4 }}>
            Microlab AI Assistant
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            You’re in <strong>{sectionLabel}</strong>. Pick a starting point:
          </Typography>
        </Box>

        <Stack spacing={1}>
          {STARTER_ACTIONS.map((action) => {
            const Icon = STARTER_ICONS[action.id] || AutoAwesomeRoundedIcon;
            return (
              <Box
                key={action.id}
                role="button"
                tabIndex={0}
                onClick={() => onStarterSelect?.(action)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onStarterSelect?.(action);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                  p: 1.4,
                  borderRadius: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(148,163,184,0.28)",
                  cursor: "pointer",
                  transition: "border-color .15s ease, box-shadow .15s ease",
                  "&:hover": {
                    borderColor: "rgba(0, 97, 254, 0.45)",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: action.comingSoon ? "#f1f5f9" : "rgba(0, 97, 254, 0.1)",
                    color: action.comingSoon ? "#64748b" : AI_ASSISTANT_BLUE,
                  }}
                >
                  <Icon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography fontWeight={700} sx={{ fontSize: 14, color: "#0f172a" }}>
                      {action.label}
                    </Typography>
                    {action.comingSoon ? (
                      <Chip
                        size="small"
                        label="Soon"
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9" }}
                      />
                    ) : null}
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.2 }}>
                    {action.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        height: "100%",
        overflowX: "hidden",
        overflowY: "auto",
        px: 2,
        py: 2,
        bgcolor: "#f8fafc",
      }}
    >
      <Stack spacing={0} sx={{ minWidth: 0, maxWidth: "100%" }}>
        {messages.map((message) => (
          <AIChatMessage key={message.id} message={message} onRetry={onRetry} />
        ))}
        {sending ? (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
            <AITypingIndicator />
          </Box>
        ) : null}
        <div ref={endRef} />
      </Stack>
    </Box>
  );
}

AIChatMessages.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  sending: PropTypes.bool,
  restoring: PropTypes.bool,
  sectionLabel: PropTypes.string,
  onRetry: PropTypes.func,
  onStarterSelect: PropTypes.func,
};
